import type { BatchPricesResponse } from "../services/pricingService";

export interface ComponentSelection {
	productId: string | null;
	productName?: string;
	productDescription?: string;
	productDescription2?: string;
	imageUrl?: string;
	availabilityQuantity?: number;
	availabilityStatus?: string;
	calculatedStockQuantity?: number;
	stockStatus?: {
		iconClass?: string;
		description?: string;
	};
	priceDifference?: {
		net: number;
		gross: number;
	};
	price?: {
		net: number;
		gross: number;
	};
}

export interface ComponentSelections {
	[productGroupId: string]: ComponentSelection | ComponentSelection[];
}

export function isMultipleSelectionEntry(
	entry: ComponentSelection | ComponentSelection[],
): entry is ComponentSelection[] {
	return Array.isArray(entry);
}

/** Normalizes a selection entry to always return an array. */
export function getSelectionsForGroup(
	selections: ComponentSelections,
	groupId: string,
): ComponentSelection[] {
	const entry = selections[groupId];
	if (!entry) return [];
	return isMultipleSelectionEntry(entry) ? entry : [entry];
}

/** Returns true if this group has at least one selected product (non-null). */
export function hasSelectionForGroup(
	selections: ComponentSelections,
	groupId: string,
): boolean {
	const entries = getSelectionsForGroup(selections, groupId);
	return entries.some((e) => e.productId !== null);
}

/**
 * Converts any scalar ComponentSelection entries for the given group IDs into
 * single-element arrays. This handles sessions stored before the Multiple-selection
 * feature was introduced, where a group's entry was always a scalar.
 */
export function normalizeMultipleSelectionEntries(
	selections: ComponentSelections,
	multipleGroupIds: Set<string>,
	knownGroupIds?: Set<string>,
): ComponentSelections {
	const normalized = { ...selections };

	for (const groupId of multipleGroupIds) {
		const entry = normalized[groupId];
		if (entry !== undefined && !Array.isArray(entry)) {
			normalized[groupId] = [entry];
		}
	}

	if (knownGroupIds) {
		for (const groupId of knownGroupIds) {
			if (multipleGroupIds.has(groupId)) continue;
			const entry = normalized[groupId];
			if (Array.isArray(entry)) {
				const firstValid = entry.find((s) => s.productId !== null);
				normalized[groupId] = firstValid ?? { productId: null };
			}
		}
	}

	return normalized;
}

export interface DefaultProductInfo {
	price: { net: number; gross: number };
	productId: string;
}

export interface DefaultProductPrices {
	[productGroupId: string]: DefaultProductInfo;
}

const STORAGE_KEY_PREFIX = "configBundle_selections";
const DEFAULT_PRICES_KEY_PREFIX = "configBundle_defaultPrices";

function getStorageKey(configBundleId: string): string {
	return `${STORAGE_KEY_PREFIX}_${configBundleId}`;
}

function getDefaultPricesKey(configBundleId: string): string {
	return `${DEFAULT_PRICES_KEY_PREFIX}_${configBundleId}`;
}

export function loadComponentSelections(configBundleId: string): ComponentSelections {
	try {
		const key = getStorageKey(configBundleId);
		const stored = sessionStorage.getItem(key);
		if (!stored) {
			return {};
		}
		return JSON.parse(stored) as ComponentSelections;
	} catch (error) {
		console.error("Failed to load component selections from sessionStorage:", error);
		return {};
	}
}

export function saveComponentSelections(
	configBundleId: string,
	selections: ComponentSelections
): void {
	const key = getStorageKey(configBundleId);
	const value = JSON.stringify(selections);

	try {
		sessionStorage.setItem(key, value);
	} catch (error) {
		if (error instanceof DOMException && error.name === "QuotaExceededError") {
			// Free space by evicting all OTHER config bundle selection entries, then retry.
			try {
				const keysToEvict: string[] = [];
				for (let i = 0; i < sessionStorage.length; i++) {
					const k = sessionStorage.key(i);
					if (k && k.startsWith(STORAGE_KEY_PREFIX) && k !== key) {
						keysToEvict.push(k);
					}
				}
				keysToEvict.forEach((k) => sessionStorage.removeItem(k));
				sessionStorage.setItem(key, value);
			} catch (retryError) {
				console.error(
					"Failed to save component selections to sessionStorage after evicting stale entries:",
					retryError,
				);
			}
		} else {
			console.error("Failed to save component selections to sessionStorage:", error);
		}
	}
}

export function updateComponentSelection(
	configBundleId: string,
	productGroupId: string,
	selection: ComponentSelection
): ComponentSelections {
	const selections = loadComponentSelections(configBundleId);
	selections[productGroupId] = selection;
	saveComponentSelections(configBundleId, selections);
	return selections;
}

export function removeComponentSelection(
	configBundleId: string,
	productGroupId: string
): ComponentSelections {
	const selections = loadComponentSelections(configBundleId);
	delete selections[productGroupId];
	saveComponentSelections(configBundleId, selections);
	return selections;
}

/**
 * For "Multiple" selection type groups: toggles the given selection in the array.
 * If the product is already selected, it is removed; otherwise it is added.
 */
export function toggleMultipleComponentSelection(
	configBundleId: string,
	productGroupId: string,
	selection: ComponentSelection,
): ComponentSelections {
	const selections = loadComponentSelections(configBundleId);
	const existing = selections[productGroupId];
	const existingArray: ComponentSelection[] = existing
		? isMultipleSelectionEntry(existing)
			? existing
			: [existing]
		: [];

	const alreadyIndex = existingArray.findIndex(
		(s) => s.productId === selection.productId,
	);
	const updated =
		alreadyIndex >= 0
			? existingArray.filter((_, i) => i !== alreadyIndex)
			: [...existingArray, selection];

	selections[productGroupId] = updated;
	saveComponentSelections(configBundleId, selections);
	return selections;
}

/**
 * For "Multiple" selection type groups: clears all selected products, storing an empty
 * array to distinguish "intentionally empty" from "never touched" (which is `undefined`).
 */
export function clearMultipleComponentSelections(
	configBundleId: string,
	productGroupId: string,
): ComponentSelections {
	const selections = loadComponentSelections(configBundleId);
	selections[productGroupId] = [];
	saveComponentSelections(configBundleId, selections);
	return selections;
}

export function clearComponentSelections(configBundleId: string): void {
	try {
		const key = getStorageKey(configBundleId);
		sessionStorage.removeItem(key);
	} catch (error) {
		console.error("Failed to clear component selections from sessionStorage:", error);
	}
}

export function clearAllSelections(): void {
	try {
		const keysToRemove: string[] = [];
		for (let i = 0; i < sessionStorage.length; i++) {
			const key = sessionStorage.key(i);
			if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
				keysToRemove.push(key);
			}
		}
		keysToRemove.forEach((key) => sessionStorage.removeItem(key));
	} catch (error) {
		console.error("Failed to clear all selections from sessionStorage:", error);
	}
}

export function loadDefaultProductPrices(configBundleId: string): DefaultProductPrices {
	try {
		const key = getDefaultPricesKey(configBundleId);
		const stored = sessionStorage.getItem(key);
		if (!stored) {
			return {};
		}
		return JSON.parse(stored) as DefaultProductPrices;
	} catch (error) {
		console.error("Failed to load default prices from sessionStorage:", error);
		return {};
	}
}

export function saveDefaultProductPrices(
	configBundleId: string,
	prices: DefaultProductPrices
): void {
	try {
		const key = getDefaultPricesKey(configBundleId);
		sessionStorage.setItem(key, JSON.stringify(prices));
	} catch (error) {
		console.error("Failed to save default prices to sessionStorage:", error);
	}
}

export function updateDefaultProductPrice(
	configBundleId: string,
	productGroupId: string,
	priceInfo: DefaultProductInfo
): DefaultProductPrices {
	const prices = loadDefaultProductPrices(configBundleId);
	prices[productGroupId] = priceInfo;
	saveDefaultProductPrices(configBundleId, prices);
	return prices;
}

export function clearDefaultProductPrices(configBundleId: string): void {
	try {
		const key = getDefaultPricesKey(configBundleId);
		sessionStorage.removeItem(key);
	} catch (error) {
		console.error("Failed to clear default prices from sessionStorage:", error);
	}
}

export function filterSelectionsToValidGroups(
	selections: ComponentSelections,
	validGroupIds: Set<string>
): ComponentSelections {
	return Object.fromEntries(
		Object.entries(selections).filter(([groupId]) => validGroupIds.has(groupId))
	);
}

export function filterDefaultPricesToValidGroups(
	defaultPrices: DefaultProductPrices,
	validGroupIds: Set<string>
): DefaultProductPrices {
	return Object.fromEntries(
		Object.entries(defaultPrices).filter(([groupId]) => validGroupIds.has(groupId))
	);
}

export function mapBatchResultsToSelections(batchResponse: BatchPricesResponse): ComponentSelections {
	const selections: ComponentSelections = {};
	const groupedResults: { [productGroupId: string]: ComponentSelection[] } = {};

	batchResponse.results.forEach(({ productGroupId, product, isAvailable }) => {
		if (isAvailable && product) {
			const selection: ComponentSelection = {
				productId: product.id,
				productName: product.title || product.description,
				productDescription: product.description,
				productDescription2: product.description2,
				imageUrl: product.imageUrl,
				availabilityQuantity: product.availabilityQuantity,
				availabilityStatus: product.availabilityStatus,
				calculatedStockQuantity: product.calculatedStockQuantity,
				stockStatus: product.stockStatus,
				priceDifference: product.priceDifference,
				price: product.price,
			};

			if (!groupedResults[productGroupId]) {
				groupedResults[productGroupId] = [];
			}
			groupedResults[productGroupId].push(selection);
		}
	});

	for (const [productGroupId, results] of Object.entries(groupedResults)) {
		// Heuristic: 1 result → scalar (Single/AlwaysIncluded); 2+ results → array (Multiple).
		// A MULTIPLE group that resolves exactly 1 product will come back as a scalar here.
		// Callers MUST follow this with normalizeMultipleSelectionEntries to upgrade those
		// scalars to arrays, otherwise toggle logic for MULTIPLE groups will misbehave.
		selections[productGroupId] = results.length === 1 ? results[0] : results;
	}

	if (batchResponse.deselections) {
		batchResponse.deselections.forEach((productGroupId) => {
			selections[productGroupId] = {
				productId: null,
			};
		});
	}

	if (batchResponse.clearedMultipleGroupIds) {
		batchResponse.clearedMultipleGroupIds.forEach((productGroupId) => {
			selections[productGroupId] = [];
		});
	}

	return selections;
}